import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Download, Share2, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { ImageCarouselProps } from '@/types/searchResults';
import { Button } from '@/components/ui/button';
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scrollLock';

import gradient2 from "@/assets/gradient_2.png";
import gradient3 from "@/assets/gradient_3.png";
import gradient4 from "@/assets/gradient_4.png";
import gradient5 from "@/assets/gradient_5.png";
import gradient6 from "@/assets/gradient_6.png";
import Illustration_1 from "@/assets/Illustration_1.jpg";
import Illustration_2 from "@/assets/Illustration_2.jpg";
import Illustration_3 from "@/assets/Illustration_3.jpg";
import Illustration_4 from "@/assets/Illustration_4.jpg";
import { getRandomGradient } from "@/assets/gradients";

const imageFallbacks = [
  gradient2,
  gradient3,
  gradient4,
  gradient5,
  gradient6,
  Illustration_1,
  Illustration_2,
  Illustration_3,
  Illustration_4,
];

const getRandomImageOrGradient = (isDarkMode: boolean) => {
  const type = Math.random() > 0.5 ? "image" : "gradient";

  if (type === "image") {
    const randomIndex = Math.floor(Math.random() * imageFallbacks.length);
    return imageFallbacks[randomIndex];
  } else {
    return getRandomGradient(isDarkMode);
  }
};

export const ImageCarousel: React.FC<ImageCarouselProps & { isOpen?: boolean; onClose?: () => void; initialIndex?: number }> = ({
  images,
  isDarkMode,
  isOpen = false, // Default to false if not provided
  onClose,
  initialIndex = 0
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const [fallbackImage] = useState(() => getRandomImageOrGradient(isDarkMode));
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const visibleImages = useMemo(() => images.filter((i) => !failedUrls.has(i.url)), [images, failedUrls]);

  // Keep currentIndex valid when images list changes (e.g. some URLs fail)
  useEffect(() => {
    if (!isOpen) return;
    if (visibleImages.length === 0) return;
    setCurrentIndex((prev) => Math.min(Math.max(0, prev), visibleImages.length - 1));
  }, [visibleImages.length, isOpen]);
  const toggleExpand = (url: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  useEffect(() => {
    setCurrentIndex(initialIndex);

    if (isOpen) {
      // Prevent background scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [initialIndex, isOpen]);

  const handleThumbnailClick = (index: number) => {
    // Dispatch custom event to open the fullscreen image modal in Index.tsx
    const event = new CustomEvent("openFullscreenImageModal", {
      detail: { images: visibleImages.map(i => ({ url: i.url, description: i.description })), initialIndex: index },
    });
    window.dispatchEvent(event);
  };

  const handleShare = async () => {
    try {
      const current = images[currentIndex];
      if (!navigator.share) return; // Web Share API not supported

      const response = await fetch(current.url);
      const blob = await response.blob();
      const fileName = current.description?.replace(/[^a-z0-9-_]+/gi, '_') || `image-${currentIndex + 1}`;
      const ext = blob.type.split('/')[1] || 'png';
      const file = new File([blob], `${fileName}.${ext}`, { type: blob.type || 'image/png' });

      if ((navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
        await navigator.share({
          title: current.description || 'Shared image',
          text: current.description || 'Check this out!',
          files: [file],
        });
      } else {
        await navigator.share({
          title: current.description || 'Shared image',
          text: current.description || 'Check this out!',
          url: current.url,
        });
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  const closeModal = () => {
    if (onClose) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeModal]);

  const handleDownload = async () => {
    try {
      const response = await fetch(images[currentIndex].url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `image-${currentIndex + 1}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const nextImage = useCallback(() => {
    setCurrentIndex((idx) => (idx < visibleImages.length - 1 ? idx + 1 : idx));
  }, [visibleImages.length]);

  const previousImage = useCallback(() => {
    setCurrentIndex((idx) => (idx > 0 ? idx - 1 : idx));
  }, []);

  // Keyboard navigation in fullscreen
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextImage();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        previousImage();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, nextImage, previousImage]);

  if (!images || images.length === 0) {
    return null;
  }

  if (!isOpen) {
    return (
      <div className="relative w-full mb-8 px-0">
        <div className="px-3 flex overflow-x-auto scroll-smooth gap-2.5 py-1 pb-3 thin-scrollbar scrollbar-thumb-gray-600 scrollbar-track-transparent">
          {visibleImages.slice(0, 5).map((img, index) => (
            <div
              key={index}
              className="flex-none max-w-40 sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)] rounded-lg overflow-hidden border-none bg-transparent transition-transform duration-200 hover:shadow-md shadow-sm h-[max-content]"
            >
              <img
                src={img.url}
                alt={img.description || "Search result image"}
                className="w-full h-36 object-cover cursor-pointer block"
                onClick={() => handleThumbnailClick(index)}
                loading="lazy"
                onError={() => {
                  setFailedUrls((prev) => new Set(prev).add(img.url));
                }}
              />
              {img.description && (
                <div className={`${isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-100"}`}>
                  <button
                    type="button"
                    className={`w-full flex items-center justify-between px-3 pt-2 text-sm ${isDarkMode ? "text-gray-300" : "text-[#2e2e2e]"}`}
                    onClick={() => toggleExpand(img.url)}
                    aria-expanded={expanded.has(img.url)}
                    aria-label="Toggle description"
                  >
                    <span className="opacity-80"></span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-300 ${expanded.has(img.url) ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <div
                    className={`px-3 pb-3 text-sm leading-relaxed overflow-auto transition-[max-height,opacity] duration-300 ${isDarkMode ? "text-gray-300" : "text-muted-foreground"}`}
                    style={{ maxHeight: expanded.has(img.url) ? '9rem' : '0', opacity: expanded.has(img.url) ? 1 : 0 }}
                  >
                    {img.description}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center transition-opacity duration-200 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      onClick={closeModal}
    >
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-black text-white transition-all duration-300"
        onClick={(e) => {
          e.stopPropagation();
          closeModal();
        }}
      >
        <X className="w-5 h-5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-16 h-10 w-10 rounded-full bg-black text-white transition-all duration-300"
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
      >
        <Download className="w-5 h-5" />
      </Button>
      {typeof navigator !== 'undefined' && navigator.share && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-28 h-10 w-10 rounded-full bg-black text-white transition-all duration-300"
          onClick={(e) => {
            e.stopPropagation();
            handleShare();
          }}
        >
          <Share2 className="w-5 h-5" />
        </Button>
      )}

      {/* Prev/Next controls */}
      {visibleImages.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black text-white transition-all duration-300"
            onClick={(e) => {
              e.stopPropagation();
              previousImage();
            }}
            disabled={currentIndex <= 0}
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black text-white transition-all duration-300"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            disabled={currentIndex >= visibleImages.length - 1}
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>
        </>
      )}

      <div
        className="max-h-[80vh] max-w-[80vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          draggable={false}
          src={visibleImages[currentIndex]?.url}
          alt={visibleImages[currentIndex]?.description || `Image ${currentIndex + 1}`}
          className="h-auto w-auto max-h-[80vh] max-w-[80vw] object-contain"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = fallbackImage;
          }}
        />
      </div>
    </div>
  );
};
